// Экран «Администрирование» (#/admin/<вкладка>[?параметры]): пользователи, приглашения, все отчёты, очередь
// сборок, журнал, настройки, место на диске. Только администратору (сервер всё равно проверяет роль: 403).
import { useEffect, useState, useSyncExternalStore } from 'react';
import { runCommand } from '../../core/ctx';
import { Btn } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { AccountMenu } from '../auth/account';
import { useAuth } from '../auth/session';
import { adminApi } from './adminApi';
import { UsersTab } from './users';
import { InvitesTab } from './invites';
import { CodesTab } from './codes';
import { ReferralsTab } from './referrals';
import { ProjectsTab } from './projects';
import { QueueTab } from './queue';
import { AuditTab } from './audit';
import { SettingsTab } from './settings';
import { StorageTab } from './storage';

const TABS: [string, string, string, string][] = [
  ['users', 'Пользователи', 'users', 'Учётные записи, роли, блокировка, пароли'],
  ['invites', 'Приглашения', 'ticket', 'Ссылки для регистрации'],
  ['codes', 'Коды активации', 'key', 'Коды на дни подписки'],
  ['referrals', 'Рефералы', 'ticket', 'Кто кого пригласил'],
  ['projects', 'Все отчёты', 'file', 'Отчёты и корзины всех пользователей'],
  ['queue', 'Очередь сборок', 'queue', 'Word: кто собирает и кто ждёт'],
  ['audit', 'Журнал', 'activity', 'Входы и действия'],
  ['settings', 'Настройки', 'settings', 'Регистрация, квоты, сеансы, сеть'],
  ['storage', 'Место на диске', 'disk', 'Сколько занимают отчёты'],
];

// ------------------------------------------------------------------------------------------------ маршрут
function subscribeHash(fn: () => void) { window.addEventListener('hashchange', fn); return () => window.removeEventListener('hashchange', fn); }
/** Текущий маршрут администрирования ({tab, query}) или null, если открыт не он. */
export function useAdminRoute(): { tab: string; query: URLSearchParams } | null {
  const hash = useSyncExternalStore(subscribeHash, () => location.hash);
  const m = hash.match(/^#\/admin(?:\/([a-z]+))?(?:\?(.*))?$/);
  if (!m) return null;
  return { tab: m[1] || 'users', query: new URLSearchParams(m[2] || '') };
}

export function AdminScreen({ tab, query }: { tab: string; query: URLSearchParams }) {
  const me = useAuth((s) => s.me);
  const [warnings, setWarnings] = useState<string[]>([]);
  useEffect(() => { document.title = 'Администрирование – Редактор СТО'; }, []);
  useEffect(() => {
    if (me?.user.role === 'admin') adminApi.server().then((s) => setWarnings(s.warnings)).catch(() => setWarnings([]));
  }, [me?.user.role, tab]);
  if (!me) return <div className="adm-page"><div className="pad adm-dim">Загрузка…</div></div>;
  if (me.user.role !== 'admin') {
    return (
      <div className="adm-page adm-denied" data-testid="admin-denied">
        <Icon name="lock" size={32} />
        <h2>Раздел только для администратора</h2>
        <Btn icon="back" label="К моим отчётам" primary onClick={() => { location.hash = '#/'; }} />
      </div>
    );
  }
  const cur = TABS.find((t) => t[0] === tab) || TABS[0];
  return (
    <div className="adm-page" data-testid="admin">
      <header className="adm-head">
        <button type="button" className="adm-back" onClick={() => { location.hash = '#/'; }} data-testid="admin-back" data-tip="К моим отчётам">
          <Icon name="back" size={16} /><span className="logo">СТО</span>
        </button>
        <div className="adm-head-title"><Icon name="shield-user" size={18} /> Администрирование</div>
        <span className="topbar-spacer" />
        <Btn icon="theme" title="Тёмная / светлая тема" onClick={() => runCommand('view.theme')} testid="admin-theme" className="topbar-icon" />
        <AccountMenu />
      </header>
      <div className="adm-body">
        <nav className="adm-nav" aria-label="Разделы администрирования">
          {TABS.map(([id, title, icon, desc]) => (
            <a key={id} href={'#/admin/' + id} className={'adm-nav-item' + (cur[0] === id ? ' is-active' : '')} data-testid={'admin-tab-' + id}
              aria-current={cur[0] === id ? 'page' : undefined}>
              <Icon name={icon} size={17} />
              <span className="adm-nav-text"><span>{title}</span><span className="adm-nav-desc">{desc}</span></span>
            </a>
          ))}
          <div className="adm-nav-foot">
            <a href="#/" className="adm-nav-item is-plain"><Icon name="file" size={17} /><span className="adm-nav-text"><span>Мои отчёты</span></span></a>
          </div>
        </nav>
        <main className="adm-main" key={cur[0]}>
          <div className="adm-main-head">
            <h1>{cur[1]}</h1>
            <span className="adm-main-desc">{cur[3]}</span>
          </div>
          {warnings.map((w) => (
            <div key={w} className="adm-warn" data-testid="admin-net-warning"><Icon name="shield-alert" size={16} /><span>{w}</span>
              {tab !== 'settings' && <Btn label="Настройки" onClick={() => { location.hash = '#/admin/settings'; }} />}</div>
          ))}
          {cur[0] === 'users' && <UsersTab />}
          {cur[0] === 'invites' && <InvitesTab />}
          {cur[0] === 'codes' && <CodesTab />}
          {cur[0] === 'referrals' && <ReferralsTab />}
          {cur[0] === 'projects' && <ProjectsTab query={query} />}
          {cur[0] === 'queue' && <QueueTab />}
          {cur[0] === 'audit' && <AuditTab query={query} />}
          {cur[0] === 'settings' && <SettingsTab />}
          {cur[0] === 'storage' && <StorageTab />}
        </main>
      </div>
    </div>
  );
}
